import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton } from '@mui/material';
import { EditExternalUserGroupForm } from 'components/Drawer/types';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useExternalUsers from 'hooks/useExternalUsers/useExternalUsers';
import useModal from 'hooks/useModal/useModal';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EditExternalUserGroupDrawer } from 'state/types';
import { lightMuiTheme } from 'theme/theme';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import EditExternalUserGroup from 'ui/organisms/Drawer/variants/EditExternalUserGroup';
import { formKeyDownHandler } from 'utils/InputOnKeyDown/formKeyDownHandler.util';

const EditExternalUserGroupVariant: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const { drawerState } = useDrawer<EditExternalUserGroupDrawer>();
  const [nameInputValue, setNameInputValue] = useState(drawerState.contentValue.name);
  const [isMaximumUserLimitEnable, setIsMaximumUserLimitEnable] = useState<boolean>(
    !!drawerState.contentValue.maxNumberOfMembers
  );
  const [maximumUserLimitValue, setMaximumUserLimitValue] = useState<number | null>(
    drawerState.contentValue.maxNumberOfMembers
  );
  const [maximumUserLimitInputValidation, setMaximumUserLimitInputValidation] = useState<string | undefined>(undefined);
  const [showValidation, setShowValidation] = useState(false);
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const { register, handleSubmit, setValue } = useForm<EditExternalUserGroupForm>();
  const { hideDrawer } = useDrawer();
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const {
    externalUsersGroups,
    updateVisitorGroupWithMaxNumberOfMembers,
    updateVisitorGroupWithMaxNumberOfMembersMutationLoading
  } = useExternalUsers({
    translation,
    handleFetchError
  });
  const { showModal } = useModal();

  const handleNameInputOnChange = (value: string) => {
    setNameInputValue(value);
  };

  const handleIsMaximumUserLimitEnableOnChange = () => {
    setShowValidation(false);
    setIsMaximumUserLimitEnable(!isMaximumUserLimitEnable);
    if (maximumUserLimitValue === null) setMaximumUserLimitValue(1);
  };

  const handleMaximumUserLimitValueOnChange = (value: number) => {
    setShowValidation(false);
    setMaximumUserLimitValue(value);
  };

  const handleDeleteExternalUserGroup = () => {
    if (drawerState.contentValue.visitorGroupId)
      showModal({
        type: 'deleteExternalUserGroupNew',
        contentValue: { visitorGroupId: drawerState.contentValue.visitorGroupId }
      });
  };

  const onSubmit = async () => {
    setShowValidation(true);
    if (
      !updateVisitorGroupWithMaxNumberOfMembersMutationLoading &&
      maximumUserLimitInputValidation === undefined &&
      nameInputValidation === undefined
    ) {
      await updateVisitorGroupWithMaxNumberOfMembers(
        nameInputValue,
        drawerState.contentValue.visitorGroupId,
        isMaximumUserLimitEnable ? maximumUserLimitValue! : null
      );
      hideDrawer();
    }
  };

  useEffect(() => {
    const numberOfMembersInGroup = externalUsersGroups.find(
      (group) => group.id === drawerState.contentValue.visitorGroupId
    )?.members.length;
    if (isMaximumUserLimitEnable && maximumUserLimitValue !== null && maximumUserLimitValue < 1)
      setMaximumUserLimitInputValidation(translation.maximum_users_cannot_be_empty);
    else if (
      isMaximumUserLimitEnable &&
      maximumUserLimitValue !== null &&
      numberOfMembersInGroup &&
      maximumUserLimitValue < numberOfMembersInGroup
    )
      setMaximumUserLimitInputValidation(translation.value_cannot_be_lower_than_current_amount_of_users_in_the_group);
    else setMaximumUserLimitInputValidation(undefined);
  }, [
    externalUsersGroups,
    drawerState.contentValue.visitorGroupId,
    isMaximumUserLimitEnable,
    maximumUserLimitValue,
    translation.value_cannot_be_lower_than_current_amount_of_users_in_the_group,
    translation.maximum_users_cannot_be_empty
  ]);

  useEffect(() => {
    if (nameInputValue === '') setNameInputValidation(translation.name_cannot_be_empty);
    else if (
      externalUsersGroups.some((group) => group.name === nameInputValue) &&
      nameInputValue !== drawerState.contentValue.name
    )
      setNameInputValidation(translation.group_name_must_be_unique);
    else setNameInputValidation(undefined);
  }, [
    drawerState.contentValue.name,
    externalUsersGroups,
    nameInputValue,
    translation.group_name_must_be_unique,
    translation.name_cannot_be_empty
  ]);

  useEffect(() => {
    setValue('name', nameInputValue);
    setValue('maxNumberOfMembers', maximumUserLimitValue);
  }, [maximumUserLimitValue, nameInputValue, setValue]);

  return (
    <DrawerTemplate
      title={translation.edit_group}
      confirmButtonText={translation.save}
      closeButtonText={translation.cancel}
      closeButton
      id="drawer-edit-external-group"
      headerSideText={
        <IconButton size="small" style={{ color: lightMuiTheme.palette.error.main }} id="drawer-delete-external-group">
          <DeleteOutlineIcon fontSize="medium" data-testid="svg-delete" style={{ marginLeft: 0 }} />
        </IconButton>
      }
      headerSideTextOnClick={handleDeleteExternalUserGroup}
      onSubmit={handleSubmit(onSubmit)}
      disableConfirmButton={showValidation && nameInputValidation !== undefined}
    >
      <EditExternalUserGroup
        register={register}
        nameInputValue={nameInputValue}
        handleNameInputOnChange={handleNameInputOnChange}
        nameInputValidation={nameInputValidation}
        switchIsMaximumUserLimitEnable={isMaximumUserLimitEnable}
        handleSwitchIsMaximumUserLimitEnableOnChange={handleIsMaximumUserLimitEnableOnChange}
        inputsMaximumUserLimitValue={maximumUserLimitValue}
        handlesMaximumUserLimitInputOnChange={handleMaximumUserLimitValueOnChange}
        showValidation={showValidation}
        inputsMaximumUserLimitValueValidation={maximumUserLimitInputValidation}
        inputOnKeyDown={(event) => formKeyDownHandler(event, onSubmit)}
      />
    </DrawerTemplate>
  );
};
export default EditExternalUserGroupVariant;
